/**
 * AWS Cognito Authentication Components
 *
 * Provides Google Sign-In functionality (via Cognito's Google federated IdP)
 * for public access to RNAverse. Drop-in replacement for the previous
 * Firebase-based FirebaseAuth.tsx.
 *
 * Uses oidc-client-ts against the Cognito user pool's OAuth2/OIDC endpoints
 * (authorization code flow with PKCE + automatic silent renew).
 */

import React, { useEffect, useState } from 'react';
import { UserManager, WebStorageStateStore, User as OidcUser } from 'oidc-client-ts';
import { APP_CONFIG } from '../config';

/** Authenticated user shape exposed to the rest of the app. */
export interface AuthUser {
  sub: string;
  email: string;
  name?: string;
  picture?: string;
}

const mapOidcUser = (user: OidcUser | null): AuthUser | null => {
  if (!user || !user.profile) {
    return null;
  }
  return {
    sub: user.profile.sub,
    email: (user.profile.email as string) || '',
    name: user.profile.name as string | undefined,
    picture: user.profile.picture as string | undefined,
  };
};

// Initialize the OIDC UserManager against the Cognito user pool
let userManager: UserManager | null = null;

try {
  if (!APP_CONFIG.cognitoAuthority || !APP_CONFIG.cognitoClientId) {
    throw new Error(
      'Cognito is not configured (REACT_APP_COGNITO_AUTHORITY / REACT_APP_COGNITO_CLIENT_ID missing)'
    );
  }

  userManager = new UserManager({
    authority: APP_CONFIG.cognitoAuthority,
    client_id: APP_CONFIG.cognitoClientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    response_type: 'code',
    scope: 'openid email profile',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });
  console.log('✅ Cognito auth initialized');
} catch (error) {
  console.error('❌ Cognito auth initialization failed:', error);
}

export { userManager };

const GOOGLE_IDP_PARAMS = { extraQueryParams: { identity_provider: 'Google' } };

/**
 * Google Sign-In Button Component
 */
export const GoogleSignInButton: React.FC<{
  onSignInSuccess?: (user: AuthUser) => void;
  onSignInError?: (error: Error) => void;
  disabled?: boolean;
}> = ({ onSignInSuccess, onSignInError, disabled }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async () => {
    if (disabled) return;
    if (!userManager) {
      console.error('Cognito auth not initialized');
      setError('Authentication is not configured');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Production: Sign in with Google via Cognito hosted popup
      const oidcUser = await userManager.signinPopup(GOOGLE_IDP_PARAMS);
      const user = mapOidcUser(oidcUser);

      if (!user) {
        throw new Error('Sign in returned no user profile');
      }

      console.log('✅ Signed in:', user.email);

      if (onSignInSuccess) {
        onSignInSuccess(user);
      }
    } catch (err: any) {
      const message: string = err?.message || '';

      // Popup blocked → fall back to full-page redirect flow
      if (/blocked/i.test(message)) {
        console.warn('Popup blocked — falling back to redirect sign-in');
        try {
          await userManager.signinRedirect(GOOGLE_IDP_PARAMS);
          return; // page navigates away
        } catch (redirectErr: any) {
          console.error('❌ Redirect sign in failed:', redirectErr);
        }
      }

      console.error('❌ Sign in failed:', err);

      const errorMessage = /closed/i.test(message)
        ? 'Sign in cancelled'
        : message || 'Failed to sign in';

      setError(errorMessage);

      if (onSignInError) {
        onSignInError(err instanceof Error ? err : new Error(message));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <button
        onClick={handleSignIn}
        disabled={isLoading || disabled}
        style={{
          padding: '12px 24px',
          fontSize: '16px',
          fontWeight: 600,
          color: '#fff',
          backgroundColor: '#4285f4',
          border: 'none',
          borderRadius: '4px',
          cursor: (isLoading || disabled) ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          opacity: (isLoading || disabled) ? 0.5 : 1,
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = '#357ae8';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#4285f4';
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18">
          <path
            fill="#fff"
            d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
          />
          <path
            fill="#fff"
            d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z"
          />
          <path
            fill="#fff"
            d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"
          />
          <path
            fill="#fff"
            d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
          />
        </svg>
        {isLoading ? 'Signing in...' : 'Sign in with Google'}
      </button>

      {error && (
        <div style={{
          marginTop: '12px',
          color: '#d32f2f',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * Sign Out Button Component
 */
export const SignOutButton: React.FC<{
  onSignOutSuccess?: () => void;
}> = ({ onSignOutSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSignOut = async () => {
    setIsLoading(true);

    try {
      if (userManager) {
        await userManager.removeUser();
      }

      console.log('✅ Signed out');

      if (onSignOutSuccess) {
        onSignOutSuccess();
      }

      // Optionally clear the Cognito hosted-UI session too
      if (APP_CONFIG.cognitoDomain && APP_CONFIG.cognitoClientId) {
        const logoutUrl =
          `${APP_CONFIG.cognitoDomain.replace(/\/$/, '')}/logout` +
          `?client_id=${encodeURIComponent(APP_CONFIG.cognitoClientId)}` +
          `&logout_uri=${encodeURIComponent(window.location.origin)}`;
        window.location.assign(logoutUrl);
      }
    } catch (error) {
      console.error('❌ Sign out failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleSignOut}
      disabled={isLoading}
      className="px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-gray-200 hover:bg-[#3e3e42] rounded transition-all"
      style={{
        cursor: isLoading ? 'not-allowed' : 'pointer',
        opacity: isLoading ? 0.7 : 1,
      }}
    >
      {isLoading ? 'Signing out...' : 'Sign out'}
    </button>
  );
};

/**
 * Hook to get current Cognito user and auth state.
 * Drop-in replacement for the previous useFirebaseAuth hook.
 */
export const useCognitoAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!userManager) {
      setLoading(false);
      return;
    }
    const manager = userManager;

    const applyUser = (oidcUser: OidcUser | null) => {
      const mapped = mapOidcUser(oidcUser);
      setUser(mapped);
      setToken(oidcUser && !oidcUser.expired ? oidcUser.id_token ?? null : null);
    };

    // Load any existing session from storage
    manager
      .getUser()
      .then((oidcUser) => {
        applyUser(oidcUser && !oidcUser.expired ? oidcUser : null);
      })
      .catch((error) => {
        console.error('Failed to load stored user:', error);
      })
      .finally(() => setLoading(false));

    // Subscribe to auth state changes (sign-in, silent renew, sign-out)
    const handleUserLoaded = (oidcUser: OidcUser) => applyUser(oidcUser);
    const handleUserUnloaded = () => applyUser(null);

    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addUserUnloaded(handleUserUnloaded);

    return () => {
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeUserUnloaded(handleUserUnloaded);
    };
  }, []);

  return { user, loading, token };
};

/**
 * Get current Cognito ID token for API calls.
 */
export const getAuthToken = async (): Promise<string | null> => {
  if (!userManager) {
    return null;
  }

  try {
    const oidcUser = await userManager.getUser();
    if (!oidcUser || oidcUser.expired) {
      return null;
    }
    return oidcUser.id_token ?? null;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
};

/**
 * Compat alias for legacy call sites (services/api.ts, services/chatApi.ts,
 * visualization components) — returns the Cognito ID token.
 */
export const getFirebaseToken = getAuthToken;
